import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import TradeHelperABI from '../../TradeHelper.json';

const GasFundManager = ({ 
  contract, 
  governanceContract, 
  setError, 
  setSuccess, 
  setLoading, 
  loading,
  onRedirectToGovernance,
  isOwner = false,
  hasBoardSeats = false,
  boardSeats = 0
}) => {
  const [gasFundBalance, setGasFundBalance] = useState('0');
  const [gasAmount, setGasAmount] = useState('');
  const [withdrawToAddress, setWithdrawToAddress] = useState('');
  
  // Fund metrics
  const [fundNav, setFundNav] = useState('0');
  const [contractNav, setContractNav] = useState('0');
  const [calculatedNav, setCalculatedNav] = useState('0');
  const [totalSupply, setTotalSupply] = useState('0');
  
  // Fee collection states
  const [lastFeeCollectionTime, setLastFeeCollectionTime] = useState(null);
  const [nextFeeCollectionTime, setNextFeeCollectionTime] = useState(null);
  const [expectedFee, setExpectedFee] = useState('0');
  const [managementFeeRate, setManagementFeeRate] = useState(0);
  const [feeStatusMessage, setFeeStatusMessage] = useState('');
  const [canCollectFee, setCanCollectFee] = useState(false);
  
  // Fee collection history
  const [feeHistory, setFeeHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Refresh counter for manual refreshes
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [positions, setPositions] = useState([]);
  
  // Transaction tracking
  const [lastTxHash, setLastTxHash] = useState('');
  const [txStatus, setTxStatus] = useState('');
  
  // Governance proposal modal state
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [proposalAction, setProposalAction] = useState({
    type: '',
    description: '',
    params: {}
  });
  
  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState({
    title: '',
    message: '',
    actionType: '',
    amount: '',
    onConfirm: () => {}
  });

  useEffect(() => {
    if (contract) {
      loadGasFundBalance();
      loadFundMetrics();
      loadFeeCollectionData();
      
      // Set up interval to refresh time remaining
      const timer = setInterval(() => {
        updateTimeRemaining();
      }, 30000); // Update every 30 seconds
      
      return () => clearInterval(timer);
    }
  }, [contract, refreshCounter]);

  // Check if user has permission (only the owner for direct contract actions)
  const hasPermission = () => {
    const hasOwnerPermission = !!isOwner;
    
    // Debug log
    console.log("Permission check (strict):", {
      isOwner: hasOwnerPermission,
      hasBoardSeats: !!hasBoardSeats,
      boardSeats: Number(boardSeats),
      hasPermission: hasOwnerPermission
    });
    
    // Only the contract owner can directly execute transactions
    return hasOwnerPermission;
  };
  
  // Common handler for all actions that might need governance approval
  const handleGovernanceAction = (actionType, description, params, directAction) => {
    // Always check permission first
    if (!hasPermission()) {
      console.log(`No permission for action: ${description}. Creating governance proposal.`);
      handleUnauthorizedAction(actionType, description, params);
      return;
    }
    
    // If we have permission, execute the direct action
    console.log(`Has permission for action: ${description}. Executing directly.`);
    directAction();
  };
  
  // Function to handle unauthorized actions by redirecting to governance
  const handleUnauthorizedAction = (actionType, description, params = {}) => {
    console.log("Creating governance proposal for:", description, params);
    
    setProposalAction({
      type: actionType,
      description,
      params
    });
    setShowProposalModal(true);
  };

  const loadGasFundBalance = async () => {
    try {
      const balance = await contract.getGasFundBalance();
      setGasFundBalance(ethers.utils.formatEther(balance));
    } catch (error) {
      console.error("Error loading gas fund balance:", error);
    }
  };
  
  // Load general fund metrics to calculate NAV
  const loadFundMetrics = async () => {
    try {
      console.log("Loading fund metrics...");
      
      // Get total supply of fund tokens
      const supply = await contract.totalSupply();
      setTotalSupply(ethers.utils.formatEther(supply));
      console.log("Total supply:", ethers.utils.formatEther(supply));
      
      // Try to get NAV from contract
      try {
        const navFromContract = await contract.calculateNAV();
        setContractNav(ethers.utils.formatEther(navFromContract));
        console.log("NAV from contract:", ethers.utils.formatEther(navFromContract));
      } catch (error) {
        console.error("Error getting NAV from contract:", error);
        setContractNav("0");
      }
      
      // Calculate NAV by summing token values
      try {
        const tokenCount = await contract.getTokenCount();
        console.log("Token count:", tokenCount.toString());
        
        let totalValue = ethers.BigNumber.from(0);
        const positionsArray = [];
        
        for (let i = 0; i < tokenCount; i++) {
          const tokenInfo = await contract.getTokenInfo(i);
          const tokenAddress = tokenInfo.tokenAddress;
          const tokenContract = new ethers.Contract(
            tokenAddress,
            ["function balanceOf(address) view returns (uint256)", "function decimals() view returns (uint8)"],
            contract.provider
          );
          
          // Get token balance
          const balance = await tokenContract.balanceOf(contract.address);
          const decimals = await tokenContract.decimals();
          
          // Get token price in ETH (this would need to be adjusted based on your actual price mechanism)
          // For now, we'll assume a dummy price of 0.01 ETH per token
          const tokenPrice = ethers.utils.parseEther("0.01"); 
          
          // Calculate value in ETH
          const value = balance.mul(tokenPrice).div(ethers.utils.parseUnits("1", decimals));
          totalValue = totalValue.add(value);
          
          positionsArray.push({
            token: tokenAddress,
            balance: ethers.utils.formatUnits(balance, decimals),
            allocation: tokenInfo.allocation.toString(),
            value: ethers.utils.formatEther(value)
          });
        }
        
        // Add ETH balance of the contract
        const ethBalance = await contract.provider.getBalance(contract.address);
        totalValue = totalValue.add(ethBalance);
        
        setCalculatedNav(ethers.utils.formatEther(totalValue));
        console.log("Calculated NAV:", ethers.utils.formatEther(totalValue));
        setPositions(positionsArray);
        
        // Set the fundNav to whichever value is higher (contract or calculated)
        const navFromContractBN = ethers.utils.parseEther(contractNav || "0");
        const calculatedNavBN = totalValue;
        
        if (calculatedNavBN.gt(navFromContractBN)) {
          setFundNav(ethers.utils.formatEther(calculatedNavBN));
        } else {
          setFundNav(ethers.utils.formatEther(navFromContractBN));
        }
        
      } catch (error) {
        console.error("Error calculating NAV:", error);
        setCalculatedNav("0");
        // If calculation fails, use contract NAV
        setFundNav(contractNav);
      }
      
    } catch (error) {
      console.error("Error loading fund metrics:", error);
    }
  };

  // Just update the time remaining without refetching everything
  const updateTimeRemaining = async () => {
    if (!contract) return;
    
    try {
      // We'll calculate time remaining manually instead of using the contract function
      const lastCollectionTime = await contract.lastFeeCollection();
      const feeCollectionInterval = await contract.FEE_COLLECTION_INTERVAL();
      
      const lastCollectionDate = new Date(lastCollectionTime.toNumber() * 1000);
      const nextCollectionDate = new Date(lastCollectionDate.getTime() + (feeCollectionInterval.toNumber() * 1000));
      const currentDate = new Date();
      
      const timeUntilNextMs = nextCollectionDate.getTime() - currentDate.getTime();
      const timeUntilNextSeconds = Math.max(0, Math.floor(timeUntilNextMs / 1000));
      
      // Update next collection time
      setNextFeeCollectionTime(nextCollectionDate.toLocaleString());
      
      // Update collection status message
      if (timeUntilNextSeconds === 0) {
        setFeeStatusMessage("Fee collection is due - can be collected now");
        setCanCollectFee(true);
      } else {
        const days = Math.floor(timeUntilNextSeconds / 86400);
        const hours = Math.floor((timeUntilNextSeconds % 86400) / 3600);
        const minutes = Math.floor((timeUntilNextSeconds % 3600) / 60);
        setFeeStatusMessage(`${days}d ${hours}h ${minutes}m until next collection`);
        setCanCollectFee(false);
      }
    } catch (error) {
      console.error("Error updating time remaining:", error);
    }
  };

  const loadFeeCollectionData = async () => {
    if (contract) {
      try {
        console.log("Loading fee collection data...");
        
        // Fetch the last fee collection time from the contract
        const lastCollectionTime = await contract.lastFeeCollection();
        console.log("Last collection time:", lastCollectionTime.toString());
        const lastCollectionDate = new Date(lastCollectionTime.toNumber() * 1000);
        setLastFeeCollectionTime(lastCollectionDate.toLocaleString());

        // Calculate the next fee collection time
        const feeCollectionInterval = await contract.FEE_COLLECTION_INTERVAL();
        console.log("Fee collection interval:", feeCollectionInterval.toString());
        
        const nextCollectionDate = new Date(lastCollectionDate.getTime() + (feeCollectionInterval.toNumber() * 1000));
        setNextFeeCollectionTime(nextCollectionDate.toLocaleString());
        
        // Calculate time until next collection
        const currentDate = new Date();
        const timeUntilNextMs = nextCollectionDate.getTime() - currentDate.getTime();
        const timeUntilNextSeconds = Math.max(0, Math.floor(timeUntilNextMs / 1000));
        
        // Update fee status message
        if (timeUntilNextSeconds === 0) {
          setFeeStatusMessage("Fee collection is due - can be collected now");
          setCanCollectFee(true);
        } else {
          const days = Math.floor(timeUntilNextSeconds / 86400);
          const hours = Math.floor((timeUntilNextSeconds % 86400) / 3600);
          const minutes = Math.floor((timeUntilNextSeconds % 3600) / 60);
          setFeeStatusMessage(`${days}d ${hours}h ${minutes}m until next collection`);
          setCanCollectFee(false);
        }

        // Fetch the management fee rate
        const managementFee = await contract.managementFee();
        console.log("Management fee rate:", managementFee.toString());
        setManagementFeeRate(managementFee.toNumber() / 100); // Convert from basis points to percentage

        // Get the TradeHelper contract address
        const tradeHelperAddress = await contract.tradeHelper();
        const signer = contract.signer;

        // Create a contract instance for TradeHelper
        const tradeHelperContract = new ethers.Contract(
          tradeHelperAddress,
          TradeHelperABI.abi,
          signer
        );

        // Calculate the expected fee - use whichever NAV value is higher
        const nav = ethers.utils.parseEther(fundNav || "0");
        try {
          const expectedFeeWei = await tradeHelperContract.calculateManagementFee(
            managementFee,
            lastCollectionTime,
            contract.address
          );
          
          console.log("Expected fee from contract:", ethers.utils.formatEther(expectedFeeWei));
          setExpectedFee(ethers.utils.formatEther(expectedFeeWei));
        } catch (error) {
          console.error("Error calculating fee from contract:", error);
          
          // Fallback to manual calculation
          const daysSinceLastCollection = 
            (currentDate.getTime() - lastCollectionDate.getTime()) / (1000 * 86400);
            
          const manualFeeCalc = parseFloat(ethers.utils.formatEther(nav)) * 
            (managementFee.toNumber() / 10000) * 
            (daysSinceLastCollection / 365);
            
          console.log("Manual fee calculation:", manualFeeCalc.toFixed(6), "ETH");
          setExpectedFee(manualFeeCalc.toFixed(6));
        }
        
        // Load fee collection history by parsing events
        try {
          const feeCollectedFilter = contract.filters.FeeCollected();
          const events = await contract.queryFilter(feeCollectedFilter, 0, 'latest');
          
          const history = await Promise.all(events.map(async (event) => {
            return {
              amount: ethers.utils.formatEther(event.args.feeAmount),
              timestamp: new Date(event.args.timestamp.toNumber() * 1000).toLocaleString(),
              txHash: event.transactionHash
            };
          }));
          
          setFeeHistory(history.reverse()); // Most recent first
          console.log("Fee history loaded:", history.length, "entries");
        } catch (error) {
          console.error("Error loading fee history:", error);
        }
        
      } catch (error) {
        console.error("Error loading fee collection data:", error);
        setError(`Error loading fee collection data: ${error.message}`);
      }
    }
  };

// Create our own version of the missing getWithdrawGasFundCallData function
const createWithdrawGasFundCallData = (amount, recipient) => {
  try {
    // Create an ethers Interface with the function definition
    const iface = new ethers.utils.Interface([
      "function withdrawGasFund(uint256 amount, address recipient)"
    ]);
    
    // Convert amount to Wei for the call data
    const amountInWei = ethers.utils.parseEther(amount);
    
    // Encode the function call with parameters
    const callData = iface.encodeFunctionData("withdrawGasFund", [amountInWei, recipient || ethers.constants.AddressZero]);
    console.log(`Generated call data for withdrawGasFund: ${callData}`);
    return callData;
  } catch (error) {
    console.error("Error creating withdraw gas fund call data:", error);
    throw error;
  }
};

// For a hypothetical withdrawGasFundTo function that allows specifying a recipient
const createWithdrawGasFundToCallData = (amount, recipient) => {
  try {
    // Create an ethers Interface with the function definition
    const iface = new ethers.utils.Interface([
      "function withdrawGasFundTo(uint256 amount, address recipient)"
    ]);
    
    // Convert amount to Wei for the call data
    const amountInWei = ethers.utils.parseEther(amount);
    
    // Encode the function call with parameters
    const callData = iface.encodeFunctionData("withdrawGasFundTo", [amountInWei, recipient]);
    console.log(`Generated call data for withdrawGasFundTo: ${callData}`);
    return callData;
  } catch (error) {
    console.error("Error creating withdraw gas fund to call data:", error);
    throw error;
  }
};

const handleAddGasFund = async () => {
  if (!gasAmount || parseFloat(gasAmount) <= 0) {
    setError("Please enter a valid amount");
    return;
  }

  setLoading(true);
  setError('');
  setSuccess('');

  try {
    // Convert amount to Wei
    const amountInWei = ethers.utils.parseEther(gasAmount);
    
    // Use the new direct function to add to gas fund
    console.log(`Adding ${gasAmount} ETH to gas fund...`);
    const tx = await contract.addToGasFund({ 
      value: amountInWei 
    });
    
    // Wait for the transaction to be mined
    console.log(`Gas fund transaction submitted: ${tx.hash}`);
    setLastTxHash(tx.hash);
    setTxStatus('Mining');
    
    const receipt = await tx.wait();
    console.log("Gas fund transaction confirmed!");
    setTxStatus('Confirmed');
    
    setSuccess(`Successfully added ${gasAmount} ETH to gas fund`);
    loadGasFundBalance();
    setGasAmount('');
  } catch (error) {
    console.error("Error adding to gas fund:", error);
    console.error("Error details:", {
        message: error.message,
        code: error.code,
        data: error?.data,
        transaction: error?.transaction
    });
    setError(error.message || "Failed to add to gas fund");
    setTxStatus('Failed');
  } finally {
    setLoading(false);
  }
};

const handleWithdrawGasFund = async () => {
  if (!gasAmount || parseFloat(gasAmount) <= 0) {
    setError("Please enter a valid amount");
    return;
  }
  
  if (parseFloat(gasAmount) > parseFloat(gasFundBalance)) {
    setError("Withdrawal amount exceeds gas fund balance");
    return;
  }
  
  // Check if this is a direct withdrawal or needs governance
  handleGovernanceAction(
    7, // WithdrawGasFund type (use appropriate type from your enum)
    `Withdraw ${gasAmount} ETH from gas fund`,
    { 
      amount: gasAmount,
      recipient: withdrawToAddress || 'contract owner' // If no address specified, default to contract owner
    },
    async () => {
      // Direct execution for contract owner
      setLoading(true);
      setError('');
      setSuccess('');
      setTxStatus('Pending');

      try {
        console.log(`Withdrawing ${gasAmount} ETH from gas fund`);
        
        // If a recipient address is specified and our contract supports it, use withdrawGasFundTo
        let tx;
        if (withdrawToAddress && contract.withdrawGasFundTo) {
          tx = await contract.withdrawGasFundTo(
            ethers.utils.parseEther(gasAmount),
            withdrawToAddress
          );
        } else {
          // Otherwise use the standard withdraw function
          tx = await contract.withdrawGasFund(ethers.utils.parseEther(gasAmount));
        }
        
        setLastTxHash(tx.hash);
        console.log(`Transaction sent: ${tx.hash}`);
        setTxStatus('Mining');
        
        await tx.wait();
        setTxStatus('Confirmed');
        
        setSuccess(`Successfully withdrawn ${gasAmount} ETH from gas fund`);
        loadGasFundBalance();
        setGasAmount('');
        setWithdrawToAddress('');
      } catch (error) {
        console.error("Error withdrawing gas fund:", error);
        setError(error.message || "Failed to withdraw gas fund");
        setTxStatus('Failed');
      } finally {
        setLoading(false);
      }
    }
  );
};

// Function to create a governance proposal for gas fund withdrawal
const createGasFundWithdrawalProposal = async () => {
  setLoading(true);
  setError('');
  setSuccess('');
  setTxStatus('Pending');
  
  try {
    // Check if governance contract is available
    if (!governanceContract) {
      console.error("Governance contract not available for creating proposal");
      
      // If we have a redirect function, use it instead
      if (typeof onRedirectToGovernance === 'function') {
        setShowProposalModal(false);
        
        // Prepare the proposal action data for redirect
        const proposalActionData = {
          type: 7, // WithdrawGasFund type
          description: withdrawToAddress 
            ? `Withdraw ${gasAmount} ETH from gas fund to ${withdrawToAddress.substring(0, 8)}...`
            : `Withdraw ${gasAmount} ETH from gas fund`,
          params: {
            amount: gasAmount,
            recipient: withdrawToAddress || 'contract owner'
          }
        };
        
        // Show a message before redirecting
        setSuccess("Redirecting to governance page to create the proposal...");
        
        // Redirect to governance page with the proposal data
        setTimeout(() => {
          onRedirectToGovernance(proposalActionData);
        }, 1500);
        
        setLoading(false);
        return;
      }
      
      // If no redirect function, show a clear error
      throw new Error("Governance contract is not available. Please go to the Governance tab to create a proposal.");
    }
    
    // Get encoded call data for gas fund withdrawal
    let callData;
    if (withdrawToAddress && contract.withdrawGasFundTo) {
      // If we need to withdraw to a specific address
      callData = createWithdrawGasFundToCallData(gasAmount, withdrawToAddress);
    } else {
      // Standard withdrawal
      callData = createWithdrawGasFundCallData(gasAmount, withdrawToAddress);
    }
    
    // Determine the description based on whether a recipient is specified
    const description = withdrawToAddress 
      ? `Withdraw ${gasAmount} ETH from gas fund to ${withdrawToAddress.substring(0, 8)}...`
      : `Withdraw ${gasAmount} ETH from gas fund`;
    
    // Create a governance proposal
    const tx = await governanceContract.createProposal(
      7, // ProposalType.WithdrawGasFund or appropriate type
      description,
      callData
    );
    
    setLastTxHash(tx.hash);
    console.log(`Proposal creation transaction sent: ${tx.hash}`);
    
    setTxStatus('Mining');
    await tx.wait();
    setTxStatus('Confirmed');
    
    setSuccess("Gas fund withdrawal proposal created successfully. It will need to be voted on and executed.");
    setGasAmount('');
    setWithdrawToAddress('');
    setShowProposalModal(false);
    
  } catch (error) {
    console.error("Error creating gas fund withdrawal proposal:", error);
    setError(error.message || "Failed to create proposal");
    setTxStatus('Failed');
  } finally {
    setLoading(false);
  }
};

const handleCollectManagementFee = async () => {
  setLoading(true);
  setError('');
  setSuccess('');
  setTxStatus('Pending');

  try {
    const tx = await contract.collectManagementFee();
    setLastTxHash(tx.hash);
    setTxStatus('Mining');
    
    await tx.wait();
    setTxStatus('Confirmed');
    
    setSuccess("Management fee collected successfully");
    
    // Refresh data
    loadFeeCollectionData();
    loadFundMetrics();
    setRefreshCounter(prev => prev + 1);
  } catch (error) {
    console.error("Error collecting management fee:", error);
    setError(error.message || "Failed to collect management fee");
    setTxStatus('Failed');
  } finally {
    setLoading(false);
  }
};
  
const handleRefresh = () => {
  setRefreshCounter(prev => prev + 1);
  loadFundMetrics();
  loadFeeCollectionData();
  loadGasFundBalance();
};

// Format address for display
const formatAddress = (address) => {
  if (!address) return '';
  return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
};

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Gas Fund Management</h2>
        <button
          className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700"
          onClick={handleRefresh}
          disabled={loading}
        >
          {loading ? "Loading..." : "Refresh Data"}
        </button>
      </div>
      
      {/* Render the proposal modal - only show if governance contract is available */}
      {showProposalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-blue-600">Create Governance Proposal</h3>
            {!governanceContract ? (
              <div>
                <p className="mb-4 text-red-600">
                  Governance contract is not available. Please go to the Governance tab to create a proposal.
                </p>
                <div className="flex justify-end">
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    onClick={() => {
                      setShowProposalModal(false);
                      if (typeof onRedirectToGovernance === 'function') {
                        onRedirectToGovernance(proposalAction);
                      }
                    }}
                  >
                    Go to Governance
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="mb-4">
                  You don't have direct permission to withdraw from the gas fund. Would you like to create a governance proposal instead?
                </p>
                <div className="mb-4 p-3 bg-gray-50 rounded">
                  <p className="font-medium">{proposalAction.description}</p>
                  
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Withdrawal Amount (ETH)
                    </label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded-md"
                      value={gasAmount}
                      onChange={(e) => setGasAmount(e.target.value)}
                      placeholder="0.1"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Recipient Address (Optional)
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded-md"
                      value={withdrawToAddress}
                      onChange={(e) => setWithdrawToAddress(e.target.value)}
                      placeholder="0x..."
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      If left empty, funds will be sent to the contract owner.
                    </p>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-4">
                  <button
                    className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
                    onClick={() => setShowProposalModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                    onClick={createGasFundWithdrawalProposal}
                    disabled={!gasAmount || parseFloat(gasAmount) <= 0 || parseFloat(gasAmount) > parseFloat(gasFundBalance) || loading}
                  >
                    {loading ? "Creating..." : "Create Proposal"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Transaction Status */}
      {lastTxHash && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="font-medium">Transaction Status:</span>
            <span className={`text-sm font-bold ${
              txStatus === 'Confirmed' ? 'text-green-600' : 
              txStatus === 'Failed' ? 'text-red-600' : 
              txStatus === 'Partial' ? 'text-yellow-600' : 'text-orange-600'
            }`}>
              {txStatus}
            </span>
          </div>
          <div className="text-xs mt-1">
            <a 
              href={`https://goerli.etherscan.io/tx/${lastTxHash}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              View on Etherscan: {lastTxHash.substring(0, 16)}...{lastTxHash.substring(lastTxHash.length - 16)}
            </a>
          </div>
        </div>
      )}
      
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="text-lg font-medium">Current Gas Fund Balance</div>
        <div className="text-2xl font-bold text-blue-600">{gasFundBalance} ETH</div>
      </div>
      
      {/* Fund NAV Information - for debugging */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-medium">Fund Net Asset Value</h3>
          <div className="text-sm text-gray-600">
            Total Supply: {parseFloat(totalSupply).toFixed(4)} tokens
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2">
          <div className="flex justify-between">
            <span>Contract NAV:</span>
            <span className="font-medium">{parseFloat(contractNav).toFixed(4)} ETH</span>
          </div>
          <div className="flex justify-between">
            <span>Calculated NAV:</span>
            <span className="font-medium">{parseFloat(calculatedNav).toFixed(4)} ETH</span>
          </div>
          <div className="flex justify-between">
            <span>Used NAV:</span>
            <span className="font-bold text-blue-600">{parseFloat(fundNav).toFixed(4)} ETH</span>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border rounded-lg p-4 border-green-200 bg-green-50">
          <h3 className="font-medium mb-2 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-600" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Add to Gas Fund
          </h3>
          <div className="bg-white rounded-lg p-3 mb-3 text-sm text-green-800">
            <p>Anyone can contribute to the gas fund. No governance approval required!</p>
            <p className="text-xs mt-1">These funds are used to pay for gas fees in automated fund operations.</p>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount (ETH)
            </label>
            <input
              type="number"
              className="w-full p-2 border rounded-md"
              value={gasAmount}
              onChange={(e) => setGasAmount(e.target.value)}
              placeholder="0.1"
              min="0"
              step="0.01"
            />
          </div>
          <button
            className="w-full bg-green-600 text-white py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
            onClick={handleAddGasFund}
            disabled={loading || !gasAmount}
          >
            {loading ? "Processing..." : "Add to Gas Fund"}
          </button>
        </div>
        
        <div className="border rounded-lg p-4 border-purple-200 bg-purple-50">
          <h3 className="font-medium mb-2 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-purple-600" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            Withdraw from Gas Fund
          </h3>
          
          <div className="bg-white rounded-lg p-3 mb-3 text-sm text-purple-800">
            {hasPermission() ? (
              <p>As the contract owner, you can withdraw funds directly.</p>
            ) : (
              <p>Withdrawals require governance approval through board member voting.</p>
            )}
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount (ETH)
            </label>
            <input
              type="number"
              className="w-full p-2 border rounded-md"
              value={gasAmount}
              onChange={(e) => setGasAmount(e.target.value)}
              placeholder="0.1"
              min="0"
              step="0.01"
            />
            <p className="mt-1 text-xs text-gray-500">
              Available balance: {gasFundBalance} ETH
            </p>
          </div>
          
          {/* Optional recipient field - only visible to contract owner or for governance */}
          {(!hasPermission() || isOwner) && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recipient Address (Optional)
              </label>
              <input
                type="text"
                className="w-full p-2 border rounded-md"
                value={withdrawToAddress}
                onChange={(e) => setWithdrawToAddress(e.target.value)}
                placeholder="0x..."
              />
              <p className="mt-1 text-xs text-gray-500">
                {isOwner ? "If left empty, funds will be sent to you (contract owner)." : 
                "Specify a recipient address for the withdrawal."}
              </p>
            </div>
          )}
          
          <button
            className={`w-full ${hasPermission() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'} text-white py-2 rounded-md disabled:bg-gray-400`}
            onClick={handleWithdrawGasFund}
            disabled={loading || !gasAmount || parseFloat(gasAmount) <= 0 || parseFloat(gasAmount) > parseFloat(gasFundBalance)}
          >
            {loading ? "Processing..." : hasPermission() ? 
              "Withdraw from Gas Fund" : 
              "Create Withdrawal Proposal"}
          </button>
          
          {/* Show governance info if user doesn't have direct permission */}
          {!hasPermission() && hasBoardSeats && (
            <div className="mt-2 p-2 bg-white rounded-lg text-sm text-purple-800 border border-purple-200">
              <p>As a board member with {boardSeats} seat(s), you can create a proposal to withdraw funds that will be voted on by other board members.</p>
            </div>
          )}
          
          {/* Show message for users without any permission */}
          {!hasPermission() && !hasBoardSeats && (
            <div className="mt-2 p-2 bg-white rounded-lg text-sm text-gray-600 border border-gray-200">
              <p>You need to be a board member or the contract owner to withdraw funds.</p>
            </div>
          )}
        </div>
      </div>

      {/* Fee Collection Information */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-medium">Management Fee Information</h3>
          <div className={`text-sm font-medium px-3 py-1 rounded-full ${canCollectFee ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
            {feeStatusMessage}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div>
              <span className="font-medium">Fee Rate:</span>
              <span className="ml-2">{managementFeeRate}% annually</span>
            </div>
            <div>
              <span className="font-medium">Last Fee Collection:</span>
              <span className="ml-2">{lastFeeCollectionTime || "Not available"}</span>
            </div>
            <div>
              <span className="font-medium">Next Fee Collection:</span>
              <span className="ml-2">{nextFeeCollectionTime || "Not available"}</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <div>
              <span className="font-medium">Current Fund NAV:</span>
              <span className="ml-2">{parseFloat(fundNav).toFixed(4)} ETH</span>
            </div>
            <div>
              <span className="font-medium">Expected Fee:</span>
              <span className="ml-2 font-bold text-blue-600">{parseFloat(expectedFee).toFixed(6)} ETH</span>
            </div>
            <div>
              <span className="font-medium">Daily Accrual:</span>
              <span className="ml-2">{(parseFloat(fundNav) * (managementFeeRate / 100) / 365).toFixed(6)} ETH</span>
            </div>
          </div>
        </div>
        
        <button
          className={`w-full ${canCollectFee ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} text-white py-2 rounded-md disabled:bg-gray-400 mt-4`}
          onClick={handleCollectManagementFee}
          disabled={loading}
        >
          {loading ? "Processing..." : "Collect Management Fee"}
        </button>
        
        {/* Fee Collection History */}
        <div className="mt-4">
          <button
            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? "Hide Fee History" : "Show Fee History"} 
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ml-1 transition-transform ${showHistory ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showHistory && (
            <div className="mt-2 max-h-40 overflow-y-auto">
              {feeHistory.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {feeHistory.map((fee, index) => (
                      <tr key={index}>
                        <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-500">{fee.timestamp}</td>
                        <td className="px-2 py-1 whitespace-nowrap text-xs text-right text-gray-900">{parseFloat(fee.amount).toFixed(6)} ETH</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-sm text-gray-500 text-center py-2">
                  No fee collection history found
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Fund Positions - for debugging */}
      {positions.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-medium mb-2">Fund Positions</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Token</th>
                  <th className="px-2 py-1 text-right text-xs font-medium text-gray-500">Balance</th>
                  <th className="px-2 py-1 text-right text-xs font-medium text-gray-500">Target %</th>
                  <th className="px-2 py-1 text-right text-xs font-medium text-gray-500">Value (ETH)</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {positions.map((position, index) => (
                  <tr key={index}>
                    <td className="px-2 py-1 whitespace-nowrap text-xs">
                      {position.token.substring(0, 6)}...{position.token.substring(position.token.length - 4)}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-right">{parseFloat(position.balance).toFixed(4)}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-right">{(parseFloat(position.allocation) / 100).toFixed(2)}%</td>
                    <td className="px-2 py-1 whitespace-nowrap text-xs text-right">{parseFloat(position.value).toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Governance Information Section */}
      {hasBoardSeats && (
        <div className="mt-6 p-4 bg-purple-50 rounded-lg">
          <h3 className="font-medium mb-2 text-purple-800">Governance Information</h3>
          <div className="text-sm text-purple-700">
            <p className="mb-2">
              As a board member, you can create proposals to withdraw from the gas fund even if you are not the contract owner.
            </p>
            <p className="mb-2">
              Your board seats: <span className="font-bold">{boardSeats}</span>
            </p>
            <p>
              These proposals will need to be voted on by other board members before being executed.
            </p>
          </div>
        </div>
      )}
      
      {/* Gas Fund Information Section */}
      <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700">
        <h4 className="font-bold">About Gas Fund Management</h4>
        <ul className="list-disc ml-5 mt-1 text-sm">
          <li className="mb-1">The Gas Fund is used to cover gas costs for automated operations such as rebalancing</li>
          <li className="mb-1"><strong>Anyone</strong> can add ETH to the Gas Fund without restriction or approval</li>
          <li className="mb-1">Only the contract owner can directly withdraw from the Gas Fund</li>
          <li className="mb-1">Board members can create governance proposals to withdraw funds</li>
          <li className="mb-1">Withdrawal proposals must be voted on by board members before execution</li>
          <li>Management fees are collected separately and not related to the Gas Fund</li>
        </ul>
      </div>
    </div>
  );
};

export default GasFundManager;